import {
  PublicPageFooter,
  PublicPageHeader,
  SkipToMainContent,
} from '@/components/public/PublicPageChrome';
import {
  CLAUDE_INTEGRATIONS_URL,
  CONNECTOR_URL,
} from '@/lib/connector';

const DOC_SECTIONS = [
  { id: 'connect', label: 'Connect' },
  { id: 'corpus', label: 'Coverage' },
  { id: 'tools', label: 'Tools' },
  { id: 'examples', label: 'Examples' },
  { id: 'tiers', label: 'Free and Pro' },
  { id: 'citation-hygiene', label: 'Citation hygiene' },
  { id: 'reporting', label: 'Report an issue' },
] as const;

export function Docs() {
  return (
    <div className="flex min-h-screen min-w-0 flex-col bg-surface-0 font-prose text-text-primary">
      <SkipToMainContent />
      <PublicPageHeader active="docs" />

      <main
        id="main-content"
        tabIndex={-1}
        className="min-w-0 flex-1 outline-none"
      >
        <article className="mx-auto max-w-[860px] min-w-0 px-6 py-14 max-mobile:px-4 max-mobile:py-10">
          <p className="font-ui text-xs uppercase tracking-[0.06em] text-text-tertiary m-0">
            Documentation
          </p>
          <h1 className="mt-3 min-w-0 font-prose text-2xl font-semibold leading-tight text-text-primary [overflow-wrap:anywhere] max-mobile:text-xl">
            Using Prometheus inside Claude
          </h1>
          <p className="mt-5 min-w-0 font-prose text-md leading-relaxed text-text-secondary [overflow-wrap:anywhere]">
            Prometheus is a Claude connector. Once you connect it, every Claude
            chat can call the connector's tools to search a public-record
            corpus of statutes, regulations, and judicial opinions, and to
            verify citations against the canonical text.
          </p>

          <DocumentationNavigation />
          <Connect />
          <Corpus />
          <Tools />
          <Examples />
          <Tiers />
          <Hygiene />
          <Reporting />
        </article>
      </main>

      <PublicPageFooter active="docs" width="reading" />
    </div>
  );
}

function DocumentationNavigation() {
  return (
    <nav
      aria-label="Documentation sections"
      className="mt-7 rounded-lg border border-border bg-surface-1 p-4"
    >
      <p className="m-0 font-ui text-xs font-semibold uppercase tracking-[0.06em] text-text-tertiary">
        On this page
      </p>
      <ul className="m-0 mt-2 flex min-w-0 list-none flex-wrap gap-x-4 gap-y-2 p-0">
        {DOC_SECTIONS.map((section) => (
          <li key={section.id}>
            <a
              href={`#${section.id}`}
              className="inline-flex min-h-10 items-center font-ui text-sm text-navy-500 underline-offset-2 hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-navy-500"
            >
              {section.label}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );
}

function Connect() {
  return (
    <Section id="connect" title="Connect Prometheus to Claude">
      <ol>
        <li>
          Open{' '}
          <a
            href={CLAUDE_INTEGRATIONS_URL}
            target="_blank"
            rel="noreferrer noopener"
            aria-label="Claude Settings Connectors (opens in a new tab)"
          >
            Claude → Settings → Connectors
            <span className="sr-only"> (opens in a new tab)</span>
          </a>
          .
        </li>
        <li>
          Click <em>Add custom connector</em>.
        </li>
        <li>Paste the connector URL:</li>
      </ol>
      <pre className="mt-3 max-w-full overflow-x-auto rounded-md border border-border bg-surface-2 px-4 py-3 font-mono text-sm text-text-primary">
        <code>{CONNECTOR_URL}</code>
      </pre>
      <ol start={4}>
        <li>
          Claude will redirect you through an OAuth 2.1 sign-in. Approve the
          requested scope.
        </li>
        <li>
          The connector is now available in any Claude conversation. Ask a
          legal question that requires primary sources and Claude will invoke
          the appropriate tool automatically.
        </li>
      </ol>
    </Section>
  );
}

function Corpus() {
  return (
    <Section id="corpus" title="What Prometheus knows">
      <p>
        The corpus is built from public-record sources. Coverage as of the
        latest index:
      </p>
      <ul>
        <li>
          <strong>17.7M judicial opinions</strong> — federal and state, every
          level of court, full text searchable.
        </li>
        <li>
          <strong>117M citation edges</strong> — the citation graph linking
          every cited authority to every citing case.
        </li>
        <li>
          <strong>900,000+ corpus chunks</strong> — statutes, regulations,
          rules, and curated case-law passages, retrieval-tuned.
        </li>
        <li>
          <strong>50 state codes</strong> — the operative statutory text of
          every U.S. state and territory.
        </li>
        <li>
          <strong>3,500+ Federal Circuit holdings</strong> — extracted as
          structured facts (claims at issue, disputed terms, citation roles)
          for IP work.
        </li>
        <li>
          <strong>U.S. Code, CFR, Federal Rules of Civil / Criminal /
          Appellate Procedure / Evidence</strong> — the canonical federal
          framework, retrievable by section.
        </li>
      </ul>
      <p>
        Coverage continues to grow. See <em>Known gaps</em> below for the
        current limitations.
      </p>
    </Section>
  );
}

const TOOLS = [
  {
    name: 'search_corpus',
    desc: 'Hybrid semantic + keyword search across the legal corpus (statutes, CFR, federal rules, MPEP, USC, etc.). Returns ranked passages with source citations.',
  },
  {
    name: 'search_cases',
    desc: 'Find cases by name, citation, or factual description. Returns case metadata (name, docket, court, decided date, reporter cite).',
  },
  {
    name: 'get_case_details',
    desc: 'Pull full case metadata and the opinion bodies (majority, dissent, concurrence) for a single case.',
  },
  {
    name: 'verify_citation',
    desc: 'Given a case name and approximate year, confirm whether the citation actually goes to the case it claims. Catches hallucinated cites.',
  },
  {
    name: 'search_cafc_holdings',
    desc: 'Semantic search across 3,500+ structured Federal Circuit holdings with doctrine tags and outcome metadata. (Pro tier — patent-vertical.)',
  },
];

function Tools() {
  return (
    <Section id="tools" title="Tools">
      <p>
        The connector exposes five tools. Claude chooses which to invoke based
        on the question — you don't need to pick.
      </p>
      <div className="mt-4 overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <caption className="sr-only">
            Prometheus connector tools and descriptions
          </caption>
          <thead>
            <tr className="bg-surface-1 border-b border-border">
              <th
                scope="col"
                className="text-left font-ui text-xs uppercase tracking-[0.04em] px-3 py-2 text-text-tertiary"
              >
                Tool
              </th>
              <th
                scope="col"
                className="text-left font-ui text-xs uppercase tracking-[0.04em] px-3 py-2 text-text-tertiary"
              >
                What it does
              </th>
            </tr>
          </thead>
          <tbody>
            {TOOLS.map((t) => (
              <tr key={t.name} className="border-b border-border-subtle">
                <td className="px-3 py-3 align-top font-mono text-xs text-text-primary whitespace-nowrap">
                  {t.name}
                </td>
                <td className="px-3 py-3 text-text-secondary font-ui text-sm leading-snug">
                  {t.desc}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Section>
  );
}

function Examples() {
  return (
    <Section id="examples" title="Worked examples">
      <p>
        Three queries you can run today, with what the connector returns and
        how Claude composes the answer.
      </p>

      <Example
        n={1}
        title="Doctrine research — Mathews v. Eldridge"
        prompt='What did SCOTUS hold in Mathews v. Eldridge about due process balancing?'
        steps={[
          'Claude calls verify_citation("Mathews v. Eldridge") to confirm the bibliographic record: 424 U.S. 319 (1976).',
          'Claude calls search_corpus to retrieve the operative passage where the Court announces the three-factor balancing test.',
          'Claude composes an answer summarizing the three factors — (1) the private interest affected, (2) the risk of erroneous deprivation under existing procedures and the value of additional safeguards, and (3) the government\'s interest — with a citation back to 424 U.S. at 335.',
        ]}
        delivers="A correct statement of the Mathews three-factor test with a verified reporter cite and a quoted passage from the opinion."
      />

      <Example
        n={2}
        title='Statutory lookup — 35 USC §112(f)'
        prompt="What's the requirement for a means-plus-function claim under 35 USC §112(f)?"
        steps={[
          'Claude calls search_corpus to retrieve the statutory text of 35 USC §112(f) governing means-plus-function claim elements.',
          'Claude calls search_cases to find Williamson v. Citrix Online, 792 F.3d 1339 (Fed. Cir. 2015) (en banc) — the controlling Federal Circuit construction of "corresponding structure" and the indefiniteness rule under §112(f).',
          'Claude composes the answer: §112(f) permits expressing a claim element as a means or step for performing a function without reciting structure, but the claim is then construed to cover the corresponding structure disclosed in the specification and equivalents thereof — failing that disclosure renders the claim indefinite under §112(b).',
        ]}
        delivers="The statutory text alongside the Williamson framework and a citation that resolves to the en banc holding."
      />

      <Example
        n={3}
        title="Citation verification — Markman v. Westview Instruments"
        prompt="Is 517 U.S. 370 actually Markman v. Westview Instruments?"
        steps={[
          'Claude calls verify_citation("Markman v. Westview Instruments", 1996) to confirm 517 U.S. 370 resolves to the right case.',
          'Claude calls get_case_details to pull the opinion bodies — the unanimous Souter opinion holding that claim construction is a question of law for the court.',
          'Claude confirms the cite, summarizes the holding, and notes the controlling Federal Circuit gloss from Phillips v. AWH Corp., 415 F.3d 1303 (Fed. Cir. 2005) (en banc), which re-anchored the claim-construction framework on intrinsic evidence.',
        ]}
        delivers="A verified answer: yes, 517 U.S. 370 is Markman, with a one-paragraph holding and the canonical follow-on case."
      />
    </Section>
  );
}

function Example({
  n,
  title,
  prompt,
  steps,
  delivers,
}: {
  n: number;
  title: string;
  prompt: string;
  steps: string[];
  delivers: string;
}) {
  return (
    <div className="mt-8 min-w-0 rounded-lg border border-border bg-surface-white p-5 max-mobile:p-4">
      <div className="flex min-w-0 flex-wrap items-baseline gap-3">
        <span
          className="shrink-0 font-ui text-xs font-semibold uppercase tracking-[0.06em] text-navy-500"
        >
          Example {n}
        </span>
        <h3 className="m-0 min-w-0 flex-1 font-prose text-md font-semibold text-text-primary [overflow-wrap:anywhere]">
          {title}
        </h3>
      </div>

      <p className="mt-4 font-ui text-xs uppercase tracking-[0.06em] text-text-tertiary m-0">
        You ask
      </p>
      <p
        className="mt-2 min-w-0 rounded-sm border-l-2 border-l-navy-500 bg-surface-1 py-2 pl-3 font-mono text-sm text-text-primary [overflow-wrap:anywhere]"
      >
        {prompt}
      </p>

      <p className="mt-4 font-ui text-xs uppercase tracking-[0.06em] text-text-tertiary m-0">
        What happens
      </p>
      <ol className="mt-2 pl-5 list-decimal flex flex-col gap-2 font-ui text-sm text-text-secondary leading-snug">
        {steps.map((s, i) => (
          <li key={i}>{s}</li>
        ))}
      </ol>

      <p className="mt-4 font-ui text-xs uppercase tracking-[0.06em] text-text-tertiary m-0">
        What you get
      </p>
      <p className="mt-2 font-ui text-sm text-text-secondary leading-snug m-0">
        {delivers}
      </p>
    </div>
  );
}

function Tiers() {
  return (
    <Section id="tiers" title="Free and Pro">
      <p>
        The connector is free for the public-record corpus. The expanded
        practitioner-grade corpus and higher query allowances are Pro.
      </p>
      <div className="mt-4 grid grid-cols-2 gap-4 max-tablet:grid-cols-1">
        <div className="border border-border rounded-md p-4 bg-surface-white">
          <h3 className="font-prose text-base font-semibold text-text-primary m-0">
            Free
          </h3>
          <ul className="mt-3 pl-5 list-disc font-ui text-sm text-text-secondary flex flex-col gap-1">
            <li>Federal and state caselaw search and citation verification</li>
            <li>USC, U.S. Constitution, Federal Rules, all 50 state codes</li>
            <li>Tribunal decisions: PTAB, NLRB, FTC, FLRA, FERC</li>
            <li>Reasonable daily query allowance</li>
          </ul>
        </div>
        <div
          className="min-w-0 rounded-md border-2 border-navy-500 bg-surface-white p-4"
        >
          <h3 className="font-prose text-base font-semibold text-text-primary m-0">
            Pro
          </h3>
          <ul className="mt-3 pl-5 list-disc font-ui text-sm text-text-secondary flex flex-col gap-1">
            <li>Everything in Free</li>
            <li>Code of Federal Regulations + agency guidance memos</li>
            <li>MPEP (Manual of Patent Examining Procedure)</li>
            <li>Patent vertical: claims, OA rejection patterns, examiner data</li>
            <li>3,500+ Federal Circuit structured holdings</li>
            <li>EDGAR + tax + SEC guidance</li>
          </ul>
        </div>
      </div>
    </Section>
  );
}

function Hygiene() {
  return (
    <Section id="citation-hygiene" title="Citation hygiene, limits, and known gaps">
      <p>
        <strong>Read the cite.</strong> Every answer Prometheus returns includes
        a citation to the underlying authority. Click through. The connector is
        designed to surface verifiable primary law; it is not a substitute for
        reading the case or the statute before relying on it.
      </p>
      <p>
        <strong>Currency.</strong> The corpus is refreshed on a rolling basis,
        but it is not real-time. Very recent opinions (typically the last few
        days) and statutory amendments may not yet appear. Use the connector's
        date metadata to confirm currency before relying on an answer for a
        time-sensitive question.
      </p>
      <p>
        <strong>Jurisdiction coverage.</strong> Federal courts at every level
        are well-covered. State court coverage is comprehensive for opinions of
        record but uneven for unpublished trial-court orders. Foreign and
        international authorities are out of scope.
      </p>
      <p>
        <strong>Known gaps.</strong> Some published opinions are missing
        reporter citations in the bibliographic record (typically
        non-precedential dispositions). Some statutory sections in the older
        state codes are present in raw text but not yet chunked for retrieval.
        Both gaps are being closed; report a specific instance via{' '}
        <em>Reporting issues</em> below and it will be prioritized.
      </p>
      <p>
        <strong>Not legal advice.</strong> Prometheus retrieves and summarizes
        primary law. It does not practice law. The answers are research
        outputs that should be reviewed by qualified counsel before they
        inform a real-world decision.
      </p>
    </Section>
  );
}

function Reporting() {
  return (
    <Section id="reporting" title="Reporting issues">
      <p>
        Mis-cites, missing authorities, or unexpected behavior — email{' '}
        <a href="mailto:adrian@prometheuslaw.app">adrian@prometheuslaw.app</a> with
        the query, the response Claude returned, and (if you can) the correct
        citation. Bug reports that include a reproducible Claude transcript
        are handled fastest.
      </p>
    </Section>
  );
}

function Section({
  id,
  title,
  children,
}: {
  id: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section aria-labelledby={id} className="mt-10 min-w-0">
      <h2
        id={id}
        tabIndex={-1}
        className="m-0 min-w-0 scroll-mt-4 font-prose text-lg font-semibold text-text-primary outline-none [overflow-wrap:anywhere]"
      >
        {title}
      </h2>
      <div className="mt-3 min-w-0 font-prose text-base leading-relaxed text-text-secondary [&_a]:break-words [&_a]:underline [&_a]:[overflow-wrap:anywhere] [&_li]:mt-1 [&_li]:[overflow-wrap:anywhere] [&_ol]:mt-3 [&_ol]:list-decimal [&_ol]:pl-5 [&_p:first-child]:mt-0 [&_p]:mt-3 [&_p]:[overflow-wrap:anywhere] [&_strong]:font-semibold [&_strong]:text-text-primary [&_ul]:mt-3 [&_ul]:list-disc [&_ul]:pl-5">
        {children}
      </div>
    </section>
  );
}
